import React, { useState } from 'react';
import './CGU.css';
import { CGU_CONFIG } from '../config/cguConfig';
import { useTranslation } from 'react-i18next';

const CGU = ({ onAccept, onDecline, showButtons = true }) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('introduction');

  const sections = [
    { id: 'introduction', title: '1. ' + t('cgu.navigation.introduction'), icon: '📋' },
    { id: 'definitions', title: '2. ' + t('cgu.navigation.definitions'), icon: '📖' },
    { id: 'services', title: '3. ' + t('cgu.navigation.services'), icon: '⚙️' },
    { id: 'access', title: '4. ' + t('cgu.navigation.access'), icon: '🔐' },
    { id: 'usage', title: '5. ' + t('cgu.navigation.usage'), icon: '💻' },
    { id: 'data', title: '6. ' + t('cgu.navigation.data'), icon: '🔒' },
    { id: 'intellectual', title: '7. ' + t('cgu.navigation.intellectual'), icon: '©️' },
    { id: 'liability', title: '8. ' + t('cgu.navigation.liability'), icon: '⚖️' },
    { id: 'modifications', title: '9. ' + t('cgu.navigation.modifications'), icon: '✏️' },
    { id: 'termination', title: '10. ' + t('cgu.navigation.termination'), icon: '🚪' },
    { id: 'law', title: '11. ' + t('cgu.navigation.law'), icon: '🏛️' }
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'introduction':
        return (
          <div className="cgu-section">
            <h3>📋 {t('cgu.introduction.title')}</h3>
            <p>
              {t('cgu.introduction.paragraph1')}
              <strong> Nodemented</strong>, {t('cgu.introduction.paragraph2')}
            </p>
            <p>
              {t('cgu.introduction.paragraph3')}
            </p>
            <div className="highlight-box">
              <strong>{t('cgu.lastUpdate')} :</strong> {CGU_CONFIG.LAST_UPDATE}<br/>
              <strong>{t('cgu.version')} :</strong> {CGU_CONFIG.CURRENT_VERSION}
            </div>
          </div>
        );

      case 'definitions':
        return (
          <div className="cgu-section">
            <h3>📖 {t('cgu.definitions.title')}</h3>
            <div className="definitions-list">
              <div className="definition-item">
                <strong>{t('cgu.service')} :</strong> {t('cgu.definitions.service')}
              </div>
              <div className="definition-item">
                <strong>{t('cgu.user')} :</strong> {t('cgu.definitions.user')}
              </div>
              <div className="definition-item">
                <strong>{t('cgu.data')} :</strong> {t('cgu.definitions.data')}
              </div>
              <div className="definition-item">
                <strong>{t('cgu.knowledgeGraph')} :</strong> {t('cgu.definitions.knowledgeGraph')}
              </div>
              <div className="definition-item">
                <strong>{t('cgu.aiLlm')} :</strong> {t('cgu.definitions.aiLlm')}
              </div>
              <div className="definition-item">
                <strong>{t('cgu.organization')} :</strong> {t('cgu.definitions.organization')}
              </div>
            </div>
          </div>
        );

      case 'services':
        return (
          <div className="cgu-section">
            <h3>⚙️ {t('cgu.services.title')}</h3>
            <p>{t('cgu.services.intro')}</p>
            
            <div className="service-category">
              <h4>🎯 {t('cgu.services.knowledgeGraphManagement')}</h4>
              <ul>
                <li>{t('cgu.services.nodeCreationEdition')}</li>
                <li>{t('cgu.services.interactiveVisualization')}</li>
                <li>{t('cgu.services.dataImportExport')}</li>
                <li>{t('cgu.services.vectorSemanticSearch')}</li>
              </ul>
            </div>

            <div className="service-category">
              <h4>🤖 {t('cgu.services.intelligentAnalysis')}</h4>
              <ul>
                <li>{t('cgu.services.autoNodeGeneration')}</li>
                <li>{t('cgu.services.sourceCodeAnalysis')}</li>
                <li>{t('cgu.services.entityExtraction')}</li>
                <li>{t('cgu.services.intelligentRelationSuggestions')}</li>
              </ul>
            </div>

            <div className="service-category">
              <h4>🧪 {t('cgu.services.testManagement')}</h4>
              <ul>
                <li>{t('cgu.services.testScenarioCreation')}</li>
                <li>{t('cgu.services.coverageTracking')}</li>
                <li>{t('cgu.services.reportGeneration')}</li>
                <li>{t('cgu.services.businessNodeIntegration')}</li>
              </ul>
            </div>
          </div>
        );

      case 'access':
        return (
          <div className="cgu-section">
            <h3>🔐 {t('cgu.access.title')}</h3>
            
            <div className="subsection">
              <h4>{t('cgu.access.conditions.title')}</h4>
              <ul>
                <li>{t('cgu.access.conditions.age')}</li>
                <li>{t('cgu.access.conditions.validEmail')}</li>
                <li>{t('cgu.access.conditions.acceptCGU')}</li>
                <li>{t('cgu.access.conditions.respectPrivacyPolicy')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>{t('cgu.access.account.title')}</h4>
              <p>
                {t('cgu.access.account.responsibilityIntro')}
              </p>
              <ul>
                <li>{t('cgu.access.account.provideAccurateInfo')}</li>
                <li>{t('cgu.access.account.secureYourAccount')}</li>
                <li>{t('cgu.access.account.notifyUnauthorizedUse')}</li>
                <li>{t('cgu.access.account.doNotShareCredentials')}</li>
              </ul>
            </div>

            <div className="warning-box">
              <strong>⚠️ {t('cgu.access.important')} :</strong> {t('cgu.access.importantNote')}
            </div>
          </div>
        );

      case 'usage':
        return (
          <div className="cgu-section">
            <h3>💻 {t('cgu.usage.title')}</h3>
            
            <div className="subsection">
              <h4>✅ {t('cgu.usage.allowed.title')}</h4>
              <ul>
                <li>{t('cgu.usage.allowed.knowledgeGraphCreation')}</li>
                <li>{t('cgu.usage.allowed.dataAnalysis')}</li>
                <li>{t('cgu.usage.allowed.collaboration')}</li>
                <li>{t('cgu.usage.allowed.dataExport')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>❌ {t('cgu.usage.prohibited.title')}</h4>
              <ul>
                <li>{t('cgu.usage.prohibited.uploadIllegalContent')}</li>
                <li>{t('cgu.usage.prohibited.attemptSystemCompromise')}</li>
                <li>{t('cgu.usage.prohibited.useForSpammingHarassment')}</li>
                <li>{t('cgu.usage.prohibited.analyzeDataWithoutAuthorization')}</li>
                <li>{t('cgu.usage.prohibited.resellServiceAccess')}</li>
                <li>{t('cgu.usage.prohibited.useAutomatedScripts')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>🔄 {t('cgu.usage.limits.title')}</h4>
              <p>
                {t('cgu.usage.limits.intro')}
              </p>
              <ul>
                <li>{t('cgu.usage.limits.apiCallLimit')}</li>
                <li>{t('cgu.usage.limits.fileSizeLimit')}</li>
                <li>{t('cgu.usage.limits.storageVolumeLimit')}</li>
                <li>{t('cgu.usage.limits.subscriptionPlanFeatures')}</li>
              </ul>
            </div>
          </div>
        );

      case 'data':
        return (
          <div className="cgu-section">
            <h3>🔒 {t('cgu.data.title')}</h3>
            
            <div className="subsection">
              <h4>{t('cgu.data.yourData.title')}</h4>
              <p>
                {t('cgu.data.yourData.paragraph1')}
              </p>
              <ul>
                <li>{t('cgu.data.yourData.protectData')}</li>
                <li>{t('cgu.data.yourData.noUnauthorizedAccess')}</li>
                <li>{t('cgu.data.yourData.exportDataAnytime')}</li>
                <li>{t('cgu.data.yourData.deleteDataOnRequest')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>{t('cgu.data.usageOfLLM.title')}</h4>
              <div className="warning-box">
                <strong>⚠️ {t('cgu.data.usageOfLLM.important')} :</strong> {t('cgu.data.usageOfLLM.note')}
              </div>
            </div>

            <div className="subsection">
              <h4>{t('cgu.data.dataIsolation.title')}</h4>
              <p>
                {t('cgu.data.dataIsolation.paragraph1')}
              </p>
            </div>

            <div className="subsection">
              <h4>{t('cgu.data.backupRecovery.title')}</h4>
              <ul>
                <li>{t('cgu.data.backupRecovery.regularBackups')}</li>
                <li>{t('cgu.data.backupRecovery.exportDataAnytime')}</li>
                <li>{t('cgu.data.backupRecovery.recoveryEfforts')}</li>
              </ul>
            </div>
          </div>
        );

      case 'intellectual':
        return (
          <div className="cgu-section">
            <h3>©️ {t('cgu.intellectual.title')}</h3>
            
            <div className="subsection">
              <h4>{t('cgu.intellectual.ourRights.title')}</h4>
              <p>
                {t('cgu.intellectual.ourRights.paragraph1')}
              </p>
              <ul>
                <li>{t('cgu.intellectual.ourRights.copyrightProhibition')}</li>
                <li>{t('cgu.intellectual.ourRights.trademarkUseProhibition')}</li>
                <li>{t('cgu.intellectual.ourRights.competingServicesProhibition')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>{t('cgu.intellectual.yourRights.title')}</h4>
              <p>
                {t('cgu.intellectual.yourRights.paragraph1')}
              </p>
              <ul>
                <li>{t('cgu.intellectual.yourRights.ownDataRights')}</li>
                <li>{t('cgu.intellectual.yourRights.knowledgeGraphsRights')}</li>
                <li>{t('cgu.intellectual.yourRights.exportsReportsRights')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>{t('cgu.intellectual.license.title')}</h4>
              <p>
                {t('cgu.intellectual.license.paragraph1')}
              </p>
            </div>
          </div>
        );

      case 'liability':
        return (
          <div className="cgu-section">
            <h3>⚖️ {t('cgu.liability.title')}</h3>
            
            <div className="subsection">
              <h4>{t('cgu.liability.limitations.title')}</h4>
              <p>
                {t('cgu.liability.limitations.paragraph1')}
              </p>
              <ul>
                <li>{t('cgu.liability.limitations.continuousAvailability')}</li>
                <li>{t('cgu.liability.limitations.accuracyOfAI')}</li>
                <li>{t('cgu.liability.limitations.noErrorsBugs')}</li>
                <li>{t('cgu.liability.limitations.compatibility')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>{t('cgu.liability.userResponsibility.title')}</h4>
              <p>{t('cgu.liability.userResponsibility.paragraph1')}</p>
              <ul>
                <li>{t('cgu.liability.userResponsibility.legalityOfData')}</li>
                <li>{t('cgu.liability.userResponsibility.respectIntellectualProperty')}</li>
                <li>{t('cgu.liability.userResponsibility.backupImportantData')}</li>
                <li>{t('cgu.liability.userResponsibility.properUseOfService')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>{t('cgu.liability.forceMajeure.title')}</h4>
              <p>
                {t('cgu.liability.forceMajeure.paragraph1')}
              </p>
            </div>
          </div>
        );

      case 'modifications':
        return (
          <div className="cgu-section">
            <h3>✏️ {t('cgu.modifications.title')}</h3>
            
            <div className="subsection">
              <h4>{t('cgu.modifications.CGUChanges.title')}</h4>
              <p>
                {t('cgu.modifications.CGUChanges.paragraph1')}
              </p>
              <ul>
                <li>{t('cgu.modifications.CGUChanges.minorImprovements')}</li>
                <li>{t('cgu.modifications.CGUChanges.majorChanges')}</li>
                <li>{t('cgu.modifications.CGUChanges.securityLegalReasons')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>{t('cgu.modifications.changeNotification.title')}</h4>
              <p>{t('cgu.modifications.changeNotification.paragraph1')}</p>
              <ul>
                <li>{t('cgu.modifications.changeNotification.emailNotification')}</li>
                <li>{t('cgu.modifications.changeNotification.appNotification')}</li>
                <li>{t('cgu.modifications.changeNotification.websitePublication')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>{t('cgu.modifications.serviceChanges.title')}</h4>
              <p>
                {t('cgu.modifications.serviceChanges.paragraph1')}
              </p>
            </div>
          </div>
        );

      case 'termination':
        return (
          <div className="cgu-section">
            <h3>🚪 {t('cgu.termination.title')}</h3>
            
            <div className="subsection">
              <h4>{t('cgu.termination.userTermination.title')}</h4>
              <p>{t('cgu.termination.userTermination.paragraph1')}</p>
              <ul>
                <li>{t('cgu.termination.userTermination.accountSettings')}</li>
                <li>{t('cgu.termination.userTermination.contactUs')}</li>
                <li>{t('cgu.termination.userTermination.immediateEffect')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>{t('cgu.termination.ourTermination.title')}</h4>
              <p>{t('cgu.termination.ourTermination.paragraph1')}</p>
              <ul>
                <li>{t('cgu.termination.ourTermination.violationOfCGU')}</li>
                <li>{t('cgu.termination.ourTermination.abusiveUse')}</li>
                <li>{t('cgu.termination.ourTermination.nonPayment')}</li>
                <li>{t('cgu.termination.ourTermination.prolongedInactivity')}</li>
              </ul>
            </div>

            <div className="subsection">
              <h4>{t('cgu.termination.consequences.title')}</h4>
              <div className="warning-box">
                <strong>⚠️ {t('cgu.termination.consequences.important')} :</strong> 
                <ul>
                  <li>{t('cgu.termination.consequences.dataDeletionAfter90Days')}</li>
                  <li>{t('cgu.termination.consequences.exportDataBeforeDeletion')}</li>
                  <li>{t('cgu.termination.consequences.immediateAccessRevocation')}</li>
                  <li>{t('cgu.termination.consequences.continuedConfidentialityObligations')}</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'law':
        return (
          <div className="cgu-section">
            <h3>🏛️ {t('cgu.law.title')}</h3>
            
            <div className="subsection">
              <h4>{t('cgu.law.jurisdiction.title')}</h4>
              <p>
                {t('cgu.law.jurisdiction.paragraph1')}
              </p>
            </div>

            <div className="subsection">
              <h4>{t('cgu.law.disputeResolution.title')}</h4>
              <p>{t('cgu.law.disputeResolution.paragraph1')}</p>
              <ol>
                <li>{t('cgu.law.disputeResolution.amicableResolution')}</li>
                <li>{t('cgu.law.disputeResolution.mediationIfNecessary')}</li>
                <li>{t('cgu.law.disputeResolution.judicialRecourseAsLastResort')}</li>
              </ol>
            </div>

            <div className="subsection">
              <h4>{t('cgu.law.severability.title')}</h4>
              <p>
                {t('cgu.law.severability.paragraph1')}
              </p>
            </div>

            <div className="subsection">
              <h4>{t('cgu.law.contact.title')}</h4>
              <div className="contact-info">
                <p>{t('cgu.law.contact.paragraph1')}</p>
                <ul>
                  <li>📧 {t('cgu.law.contact.email')}: nodemented.contact@gmail.com</li>
                  <li>📱 {t('cgu.law.contact.appSupport')}</li>
                  <li>🌐 {t('cgu.law.contact.onlineDocumentation')}</li>
                </ul>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="cgu-container">
      <div className="cgu-header">
        <h1>{t('cgu.title')}</h1>
        <p>{t('cgu.subtitle')}</p>
      </div>

      <div className="cgu-content">
        <div className="cgu-sidebar">
          <h3>📑 {t('cgu.summary')}</h3>
          <nav className="cgu-nav">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="nav-icon">{section.icon}</span>
                <span className="nav-title">{section.title}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="cgu-main">
          {renderSection()}
        </div>
      </div>

      {showButtons && (
        <div className="cgu-actions">
          <button className="btn-decline" onClick={onDecline}>
            ❌ {t('cgu.decline')}
          </button>
          <button className="btn-accept" onClick={onAccept}>
            ✅ {t('cgu.accept')}
          </button>
        </div>
      )}
    </div>
  );
};

export default CGU;
